<template>
  <section class="create-organization">
    <header>
      <h1>New Organization</h1>
    </header>
    <form :class="{ dirty: $v.$anyDirty }" autocomplete="off" @submit.prevent="submit">
      <div class="form-row">
        <p-text-field v-model="name" :label="'Name'" autocomplete="off" :disabled="isCreateRequestPending" />
        <div v-if="!$v.name.required" class="error">Name is required</div>
      </div>
      <div class="form-row">
        <p-tags v-model="aliases" :label="'Aliases'" :disabled="isCreateRequestPending" placeholder="" :options="[]" />
      </div>
      <div class="form-row">
        <p-text-field v-model="shortName" :label="'Prefix'" autocomplete="off" :disabled="isCreateRequestPending" />
        <div v-if="!$v.shortName.required" class="error">Short name is required</div>
      </div>
      <div class="form-row">
        <p-multiselect
          v-model="type"
          :options="organizationTypeOptions"
          :get-label-callback="v => v"
          label="Organization Type"
          placeholder="Select type"
          :searchable="false"
          :multiple="false"
        />
      </div>
      <div class="form-row">
        <p-multiselect
          v-model="models"
          :options="getModelOptions()"
          :get-label-callback="getModelName"
          :disabled="!$hasPermission('administration')"
          label="Models (specify models that this organization can use)"
          placeholder="Select models"
          :searchable="true"
          :multiple="true"
        ></p-multiselect>
      </div>
      <div class="form-row">
        <p-tags v-model="domains" :label="'Correspondence domains'" :disabled="isCreateRequestPending" placeholder="" :options="[]" />
      </div>
      <div class="form-row">
        <p-tags v-model="regexs" :label="'Regexs'" :disabled="isCreateRequestPending" placeholder="" :options="[]" />
      </div>
      <div class="form-row">
        <p-checkbox v-model="enableSpeechToText" :label="'Enable speech to text'" :disabled="isCreateRequestPending" />
      </div>
      <div v-if="enableSpeechToText" class="form-row">
        <p-multiselect
          v-model="selectedSpeechService"
          :options="speechToTextOptions"
          :get-label-callback="getSpeechServiceLabel"
          :disabled="isCreateRequestPending"
          label="Speech to text service/model"
          placeholder="Select speech to text service or model"
          :searchable="true"
          :multiple="false"
        />
      </div>
      <div v-if="!$v.selectedSpeechService.required && $v.selectedSpeechService.$dirty && enableSpeechToText" class="error">
        Speech to text service/model is required
      </div>
      <div v-if="enableSpeechToText && isSpeechModelSelected" class="form-row">
        <p-multiselect
          v-model="selectedSpeechPostProcessingModel"
          :options="chatModelsOptions"
          :get-label-callback="getChatModelLabel"
          :disabled="isCreateRequestPending"
          label="Speech post processing model"
          placeholder="Select chat model"
          :searchable="true"
          :multiple="false"
        />
      </div>
      <div class="form-row binding-section">
        <label>Binding Constructors</label>
        <div v-for="(bc, i) in bindingConstructors" :key="i" class="bc-row">
          <p-text-field v-model="bc.name" :label="'Name'" :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')" />
          <p-text-field v-model="bc.valueTemplate" :label="'Value Template'" :disabled="isUpdateRequestPending || !$hasPermission('organizations.write')" />
          <p-button v-if="$hasPermission('organizations.write')" type="button"   @click="removeBindingConstructor(i)">
                <MdIcon size="sm" name="delete" />
          </p-button>
        </div>
        <p-button v-if="$hasPermission('organizations.write')" type="button" color="primary" variant="text" @click="addBindingConstructor">Add Constructor</p-button>
      </div>
      <div v-if="isCreateRequestFailed" class="error">{{ errorMessage }}</div>
      <div class="form-row submit">
        <p-button type="button" @click.prevent="$router.back()">Cancel</p-button>
        <p-button color="primary" type="submit" :disabled="isCreateRequestPending || ($v.$anyDirty && $v.$invalid)">Save</p-button>
      </div>
    </form>

    <footer></footer>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';

import TextField from '@/components/common/TextField';
import Tags from '@/components/common/Tags';
import Button from '@/components/common/Button';
import Multiselect from '@/components/common/Multiselect';
import CheckBox from '@/components/common/Checkbox.vue';
import MdIcon from '@/components/common/MdIcon';

export default {
  components: {
    'p-text-field': TextField,
    'p-tags': Tags,
    'p-button': Button,
    'p-checkbox': CheckBox,
    'p-multiselect': Multiselect,
    MdIcon,
  },
  data() {
    return {
      name: '',
      aliases: [],
      shortName: '',
      domains: [],
      regexs: [],
      models: [],
      type: null,
      errorMessage: '',
      enableSpeechToText: false,
      selectedSpeechService: null,
      selectedSpeechPostProcessingModel: null,
      bindingConstructors: []
    };
  },
  computed: {
    ...mapState({
      isCreateRequestPending: s => s.organizations.isCreateRequestPending,
      isCreateRequestFailed: s => s.organizations.isCreateRequestFailed,
      modelsCollection: s => s.nlp.models ?? [],
      sttServices: s => s.nlp.sttServices ?? []
    }),
    speechToTextOptions() {
      const service = this.sttServices?.map(s => ({ id: s.id, label: s.name, type: 'service' })) ?? [];

      const ids = Array.isArray(this.models) ? this.models.filter(x => typeof x === 'string') : [];

      const selectedModels = ids.map(id => this.modelsCollection.find(m => m.id === id)).filter(Boolean);

      const speechModels = selectedModels
        .filter(m => {
          if (Array.isArray(m.capabilities)) {
            return m.capabilities.some(cap => typeof cap === 'string' && cap.includes('speech-to-text'));
          }
          if (typeof m.capability === 'string') {
            return m.capability.includes('speech-to-text');
          }
          return false;
        })
        .map(m => ({
          id: m.id,
          label: m.name,
          type: 'model'
        }));

      return [...service, ...speechModels];
    },
    chatModelsOptions() {
      const ids = Array.isArray(this.models) ? this.models.filter(x => typeof x === 'string') : [];

      const selectedModels = ids.map(id => this.modelsCollection.find(m => m.id === id)).filter(Boolean);

      return selectedModels
        .filter(m => {
          if (Array.isArray(m.capabilities)) {
            return m.capabilities.some(cap => typeof cap === 'string' && cap.includes('chat'));
          }
          if (typeof m.capability === 'string') {
            return m.capability.includes('chat');
          }
          return false;
        })
        .map(m => ({ id: m.id, label: m.name }));
    },
    isSpeechModelSelected() {
      if (!this.selectedSpeechService) return false;
      const opt =
        typeof this.selectedSpeechService === 'object'
          ? this.selectedSpeechService
          : this.speechToTextOptions.find(x => x.id === this.selectedSpeechService);
      return opt && opt.type === 'model';
    },
    organizationTypeOptions() {
      return ['Client', 'Firm', 'Other'];
    }
  },
  validations: {
    name: {
      required
    },
    shortName: {
      required
    },
    selectedSpeechService: {
      required: requiredIf(function() {
        return this.enableSpeechToText;
      })
    }
  },
  watch: {
    speechToTextOptions(newOptions) {
      if (
        this.selectedSpeechService &&
        !newOptions.some(opt =>
          typeof this.selectedSpeechService === 'object' ? opt.id === this.selectedSpeechService.id : opt.id === this.selectedSpeechService
        )
      ) {
        this.selectedSpeechService = null;
      }
    },
    chatModelsOptions(newOptions) {
      if (
        this.selectedSpeechPostProcessingModel &&
        !newOptions.some(opt =>
          typeof this.selectedSpeechPostProcessingModel === 'object'
            ? opt.id === this.selectedSpeechPostProcessingModel.id
            : opt.id === this.selectedSpeechPostProcessingModel
        )
      ) {
        this.selectedSpeechPostProcessingModel = null;
      }
    }
  },
  async created() {
    if (!this.modelsCollection?.length || !this.sttServices?.length) {
      await this.$store.dispatch('nlp/getConfigurations');
      await this.$store.dispatch('nlp/getSttServices');
    }
  },
  methods: {
    getSpeechServiceLabel(option) {
      if (typeof option === 'string') {
        const found = this.speechToTextOptions.find(opt => opt.id === option);
        return found ? found.label : option;
      }
      return option.label || '';
    },
    getChatModelLabel(option) {
      if (typeof option === 'string') {
        const found = this.chatModelsOptions.find(opt => opt.id === option);
        return found ? found.label : option;
      }
      return option.label || '';
    },
    getModelOptions() {
      return this.modelsCollection?.map(m => m.id) ?? [];
    },
    getModelName(id) {
      return this.modelsCollection.find(model => model.id === id)?.name ?? 'Unknown Model';
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      try {
        this.errorMessage = '';

        let speechToTextService = null;
        if (this.enableSpeechToText && this.selectedSpeechService) {
          if (typeof this.selectedSpeechService === 'object') {
            speechToTextService = {
              id: this.selectedSpeechService.id,
              type: this.selectedSpeechService.type
            };
          } else {
            const found = this.speechToTextOptions.find(opt => opt.id === this.selectedSpeechService);
            speechToTextService = found ? { id: found.id, type: found.type } : { id: this.selectedSpeechService, type: null };
          }
        }

        let speechPostProcessingModel = null;
        if (this.enableSpeechToText && this.isSpeechModelSelected && this.selectedSpeechPostProcessingModel) {
          if (typeof this.selectedSpeechPostProcessingModel === 'object') {
            speechPostProcessingModel = {
              id: this.selectedSpeechPostProcessingModel.id,
              type: this.selectedSpeechPostProcessingModel.type || 'model'
            };
          } else {
            const found = this.chatModelsOptions.find(opt => opt.id === this.selectedSpeechPostProcessingModel);
            speechPostProcessingModel = found
              ? { id: found.id, type: found.type || 'model' }
              : { id: this.selectedSpeechPostProcessingModel, type: 'model' };
          }
        }

        await this.$store.dispatch('organizations/create', {
          name: this.name.trim(),
          shortName: this.shortName.trim(),
          aliases: Array.isArray(this.aliases) ? this.aliases : [],
          domains: Array.isArray(this.domains) ? this.domains : [],
          regexs: Array.isArray(this.regexs) ? this.regexs : [],
          models: Array.isArray(this.models) ? this.models : [],
          type: this.type || undefined,
          enableSpeechToText: !!this.enableSpeechToText,
          speechToTextService,
          speechPostProcessingModel,
          bindingConstructors: (this.bindingConstructors || []).filter(x => x && x.name)
        });
        this.$router.push({ path: `/organizations` });
      } catch (e) {
        this.errorMessage =
          e?.response?.status === 409 ? 'Organization already exists' : 'Failed to create a new organization. Check your input and try again.';
      }
    },
    back() {
      this.$router.back();
    },
    addBindingConstructor() {
      this.bindingConstructors.push({ name: '', valueTemplate: '' });
    },
    removeBindingConstructor(i) {
      this.bindingConstructors.splice(i, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.create-organization {
  width: 100%;
  header {
    width: 100%;
    padding: 1rem 2rem;
    border-bottom: 1px solid var(--theme-highlight);
    box-sizing: border-box;
  }

  h1 {
    color: var(--theme-on-surface);
    font-size: 1.5rem;
    font-weight: 700;
  }
  form {
    padding: 2rem;
    max-width: 480px;

    > * {
      &:not(:last-child) {
        margin-bottom: 1.5rem;
      }
      &:last-child {
        display: flex;
        justify-content: flex-end;
      }
    }

    .error {
      font-size: 0.8rem;
      color: var(--theme-error);
      text-align: left;
      padding: 0.25rem 0;
      display: none;
    }
    &.dirty {
      .error {
        display: block;
      }
    }
  }

.binding-section {
    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--theme-on-surface);
    }
  }

  .bc-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto; 
    gap: 0.75rem;
    align-items: end; 
    margin-bottom: 0.75rem;

    ::v-deep(.text-field) {
      width: 100%;
    }

    ::v-deep(button) {
      white-space: nowrap;
      height: 38px;
    }
  }
}
</style>
